import React, { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TestResult {
    envelope: string;
    testFile: string;
    status: string;
}

export interface Test {
    id: string;
    name: string;
    description: string;
    results: TestResult[];
}

export interface TestExecutionData {
    overallStatus: string;
    summary: string;
    tests: Test[];
}

interface TestExecutionProps {
    testExecution?: TestExecutionData;
    locked?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SPACING = {
    betweenCards: 3,
    withinCard: 2,
    betweenEqual: 1,
};

const getStatusColor = (status: string): any =>
    status === 'Success' ? 'success' : 'criticalError';

// ─── Component ────────────────────────────────────────────────────────────────

const TestExecution: React.FC<TestExecutionProps> = ({ testExecution, locked = false }) => {
    const [expandedTest, setExpandedTest] = useState<string | false>(
        testExecution?.tests?.[0]?.id || false
    );

    const handleTestChange = (id: string) => (_e: React.SyntheticEvent, isExpanded: boolean) =>
        setExpandedTest(isExpanded ? id : false);

    return (
        <Box sx={{ mt: SPACING.betweenCards, position: 'relative' }}>
            {locked && (
                <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 1,
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    borderRadius: '8px',
                    cursor: 'not-allowed',
                }} />
            )}
            <Accordion
                defaultExpanded={!locked}
                sx={{
                    borderRadius: '8px !important',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1) !important',
                    border: 'none',
                    '&:before': { display: 'none' },
                    '&.MuiAccordion-root': { border: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                        <Typography variant="h2" sx={{ color: 'rgba(0,0,0,0.87)' }}>Test Execution</Typography>
                        {testExecution && (
                            <Chip
                                label={testExecution.overallStatus}
                                color={testExecution.overallStatus === 'Failed' ? 'criticalError' : 'success'}
                                size="small"
                                variant="badge"
                            />
                        )}
                        {/* {locked && !testExecution && <Chip label="Pending" color="passive" variant="badge" size="small" />} */}
                        {locked && !testExecution && (
                            <Typography variant="bodySmall" color="text.secondary">
                                Complete the Staging stage to unlock test execution.
                            </Typography>
                        )}
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: testExecution ? SPACING.betweenCards : 0 }}>
                    {testExecution ? (
                    <Paper sx={{ p: SPACING.withinCard, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: SPACING.withinCard, mb: SPACING.withinCard, flexWrap: 'wrap' }}>
                            <Typography variant="bodyMedium">Test Results</Typography>
                            <Typography variant="bodySmall" color="text.secondary">
                                {testExecution.summary}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: SPACING.withinCard }}>
                            {testExecution.tests.map((test) => (
                                <Accordion
                                    key={test.id}
                                    expanded={expandedTest === test.id}
                                    onChange={handleTestChange(test.id)}
                                    disableGutters
                                    sx={{
                                        border: '1px solid #d2d6db',
                                        borderRadius: '8px !important',
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                        overflow: 'hidden',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            px: SPACING.withinCard,
                                            py: 0.5,
                                            '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="labelLarge" component="div" sx={{ display: 'block' }}>
                                                    {test.name}
                                                </Typography>
                                                <Typography variant="labelSmall" component="div" color="text.secondary" sx={{ display: 'block' }}>
                                                    {test.description}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, ml: 'auto' }}>
                                                <Typography variant="bodyMedium" color="text.secondary">
                                                    {test.results.length} Test Files
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ px: 0, py: 0, backgroundColor: '#fcfcfc' }}>
                                        <Box sx={{ px: SPACING.withinCard, py: SPACING.withinCard }}>
                                            <Box sx={{ display: 'flex', pb: 1.5, color: '#1f1f1f', fontWeight: 700 }}>
                                                <Box sx={{ flexBasis: '44%', maxWidth: '44%' }}>
                                                    <Typography variant="labelMedium">Envelope</Typography>
                                                </Box>
                                                <Box sx={{ flexBasis: '38%', maxWidth: '38%' }}>
                                                    <Typography variant="labelMedium">Test File</Typography>
                                                </Box>
                                                <Box sx={{ flexBasis: '18%', maxWidth: '18%' }}>
                                                    <Typography variant="labelMedium">Status</Typography>
                                                </Box>
                                            </Box>
                                            <Divider />
                                            {test.results.map((result, index) => (
                                                <React.Fragment key={`${test.id}-result-${index}`}>
                                                    <Box sx={{ display: 'flex', py: 1.5, alignItems: 'center' }}>
                                                        <Box sx={{ flexBasis: '44%', maxWidth: '44%' }}>
                                                            <Typography variant="bodyMedium">{result.envelope}</Typography>
                                                        </Box>
                                                        <Box sx={{ flexBasis: '38%', maxWidth: '38%' }}>
                                                            <Link href="#" underline="hover" color="primary" sx={{ fontSize: '0.875rem' }}>
                                                                {result.testFile}
                                                            </Link>
                                                        </Box>
                                                        <Box sx={{ flexBasis: '18%', maxWidth: '18%' }}>
                                                            <Chip label={result.status} color={getStatusColor(result.status)} size="small" variant="badge" />
                                                        </Box>
                                                    </Box>
                                                    <Divider />
                                                </React.Fragment>
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING.betweenEqual, mt: SPACING.betweenCards, flexWrap: 'wrap' }}>
                                <Button variant="primary">Re-run Tests</Button>
                                <Button variant="secondary">Rollback Staging</Button>
                            </Box>
                        </Box>
                    </Paper>
                    ) : null}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default TestExecution;
